import StateEventManager        from "@/js/Shared/Events/Managers/StateEventManager";
import BroadcastEventManager    from "@/js/Shared/Events/Managers/BroadcastEventManager";
import MeoCloudHTTPService      from "./HTTP/MeoCloudHTTPService";
import MeoCloudHTTPMapping      from "./HTTP/MeoCloudHTTPMapping";
import Controller               from "../Shared/Controllers/Controller";
import NotificationsController  from "@/js/Webmail/Notifications/NotificationsController";
import PopupControllerSingleton from "../Webmail/PopupController";
import SAPOMailConfig           from "@/SAPOMailConfig";
import EventUpload from "../Shared/Events/EventUpload";
import MeoCloudUploadItem from "./Entities/MeoCloudUploadItem";

export {MeoCloudController as default};

/**
* HouseKeeper Controller
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class MeoCloudController extends Controller {

  /**
   * Compose Event State
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {StateEventManager}
   */
  state = new StateEventManager();

  /**
   * Defines the mapped contents retrieved by the API
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Object}
   */
  data = {};

  /**
   * Defines an instance of MeoCloudHTTPService.
   *
   * @private
   *
   * @since 7.0.0
   *
   * @type {MeoCloudHTTPService}
   */
  http_service = new MeoCloudHTTPService();

  /**
   * Defines an instance of MeoCloudHTTPMapping.
   *
   * @private
   *
   * @since 7.0.0
   *
   * @type {MeoCloudHTTPMapping}
   */
  http_mapping = new MeoCloudHTTPMapping();

  /**
   * Event Broadcaster
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {BroadcastEventManager}
   */
  broadcaster = new BroadcastEventManager();

  /**
   * MeoCloud Uploads
   *
   * @public
   *
   * @type {MeoCloudUploadItem}
   *
   * @since 7.0.0
   */
  uploads = new MeoCloudUploadItem();

  /**
   * MeoCloud upload files
   *
   * @public
   *
   * @type {Array}
   *
   * @since 7.0.0
   *
   */
  uploaded_files = [];

  /**
   * Gets Main Cloud Data
   *
   * @public
   *
   * @since 7.0.0
   *
   * @returns {Promise<Boolean>} Indicates whether the data retrieval was successful.
   */
  async getCloudData(type = 'full') {
    this.section_state.setLoading('meo_cloud');
    let response = await this.http_service.authorize();

    NotificationsController.addMessage('notifications.', response.messages);
    this.http_mapping.mapResponse(this.data, response.results, type);

    this.section_state.setLoaded('meo_cloud', response.success);
    return response.success;
  }

  /**
   * Creates a new folder with the specified name.
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} folder_name - The name of the folder to create.
   *
   * @returns {Promise<Boolean>} Indicates whether the folder creation was successful.
   */
  async createFolder(folder_name) {
    this.saving_state.setSaving('create_folder');
    let response = await this.http_service.createFolder(
      this.data.metadata.path || '/',
      folder_name,
    );

    NotificationsController.addMessage('notifications.', response.messages);
    this.http_mapping.mapResponse(this.data, response.results);

    this.saving_state.unsetSaving('create_folder');
    return response.success;
  }

  /**
   * Browses the contents of the newly created folder.
   *
   * @public
   *
   * @since 7.0.0
   *
   * @returns {Promise<Boolean>} Indicates whether the browsing was successful.
   */
  async browseNewFolder() {
    return this.browse(this.data.metadata.path || '/');
  }

  /**
   * Browses the contents of the specified path.
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} path - The path to browse.
   *
   * @returns {Promise<Boolean>} Indicates whether the browsing was successful.
   */
  async browse(path, type = 'full') {
    this.section_state.setLoading('meo_cloud');
    let response = await this.http_service.browse(path);

    NotificationsController.addMessage('notifications.', response.messages);
    this.http_mapping.mapResponse(this.data, response.results, type);

    this.section_state.setLoaded('meo_cloud', response.success);
    return response.success;
  }

  /**
   * Searches for the specified term in the specified path.
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} search_term     The term to search for.
   * @param {String} search_criteria The criteria to use for the search.
   *
   * @returns {Promise<Boolean>} Indicates whether the search was successful.
   */
  async search(search_term, search_criteria) {
    this.section_state.setLoading('meo_cloud');
    let response = await this.http_service.search(
      this.data.metadata.path || '/',
      search_term,
      search_criteria,
    );

    NotificationsController.addMessage('notifications.', response.messages);
    this.http_mapping.mapResponse(this.data, response.results);

    this.section_state.setLoaded('meo_cloud', response.success);
    return response.success;
  }

  /**
   * Uploads an attachment to the Meo Cloud in the current path.
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Attachment} attachment The attachment to upload.
   *
   * @returns {Promise<Boolean>} Indicates whether the upload was successful.
   */
  async uploadAttachmentToCloud(attachment) {
    this.saving_state.setSaving('meo_cloud_attachment_upload');

    let response = await this.http_service.uploadAttachmentToCloud(
      attachment,
      this.data.metadata.path + '/' || '/',
    );

    NotificationsController.addMessage('notifications.', response.messages);
    this.saving_state.unsetSaving('meo_cloud_attachment_upload');

    return response.success;
  }

  /**
   * Removes the authorization for the Meo Cloud.
   *
   * @public
   *
   * @since 7.0.0
   *
   * @returns {Boolean} Indicates whether the authorization removal was successful.
   *
   * @returns {Promise<Boolean>}
   */
  async removeAuthorization() {
    this.section_state.setLoading('meo_cloud');
    let response = await this.http_service.removeAuthorization();

    NotificationsController.addMessage('notifications.', response.messages);
    this.http_mapping.mapResponse(this.data, response.results);

    this.section_state.setLoaded('meo_cloud', response.success);
    return response.success;
  }

  /**
   * Opens the authorization window for the Meo Cloud.
   *
   * @public
   *
   * @since 7.0.0
   *
   * @returns {Promise<void>}
   */
  async openAuthorization() {
    PopupControllerSingleton.openExternal({
      path: this.http_service.endpoint + '?goVerify&view_token=' + SAPOMailConfig.server.security.csrf_token,
    })
  }

  /**
   * Gets all public links for the specified files.
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Array} files       The files to get the public links for.
   * @param {String} expiration The expiration time for the public links.
   *
   * @returns {Promise<Object>} The public links for the specified files.
   */
  async getSharedLinks(files, expiration) {
    let shared_files = [];

    for (let file of files) {
      let item = {
        file_path : file.path,
        file_id   : file.rev,
      }

      shared_files.push(item);
    }

    this.saving_state.setSaving('get_shared_links');
    let response = await this.http_service.shareFiles(shared_files, expiration);
    NotificationsController.addMessage('notifications.', response.messages);
    this.saving_state.unsetSaving('get_shared_links');
    return response;
  }

  /**
   * Adds files to the Meo Cloud
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FilesEvent}            files          The files to add to the Meo Cloud.
   * @param {SimpleCollectionList}  global_uploads list of Global Uploads
   *
   * @return {Promise<Boolean>}
   */
    async addFiles(files, global_uploads) {
      if (this.validateFileLimits(files) === false) {
        return false;
      }

      this.uploads = new MeoCloudUploadItem();
      this.uploads.createEventsFromFiles(files);

      for (let event of this.uploads.events) {
        this.uploads.event = event;
        this.uploads.current++;
        global_uploads.add(event, event.id);
        let response = await this.http_service.signUpload(this.data.metadata.path, event.file.name);

        if (response.success === false) {
          global_uploads.delete(event.id);
          NotificationsController.addError('notifications.compose_meo_cloud_file_error', [event.file.name]);
          continue;
        }

        let upload = await this.uploadFile(event, response.results.signature, global_uploads);

        if (upload.success === false) {
          NotificationsController.addError('notifications.compose_meo_cloud_file_error', [event.file.name]);
          continue;
        }

        this.uploaded_files.push(
          this.http_mapping.mapUploads(upload.results)
        );
      };

      this.uploads.finished = true;
      return;
    }

  /**
   * Uploads a single file to meo cloud
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {EventUpload} event
   * @param {Object}      signature file signature
   *
   * @return {Promise<void>}
   */
  async uploadFile(event, signature, global_uploads) {
    return new Promise((resolve) => {
      event.broadcaster.on('upload-finished', (response) => {
        global_uploads.delete(event.id)
        return resolve(response);
      })

      this.http_service.uploadFile(event, signature);
    })
  }

  /**
   * Aborts the current upload process
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {SimpleCollectionList}  global_uploads list of Global Uploads
   *
   * @return {Promise<void>}
   */
  async abortUpload(global_uploads) {
    global_uploads.delete(this.uploads.event.id)
    this.uploads.event.xhr.abort();
    this.uploads = new MeoCloudUploadItem();
  }

  /**
   * Resets the uploaded files
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  resetUplodedFiles() {
    if (this.uploaded_files.length === 0) {
      return;
    }

    this.uploaded_files = [];
  }

  /**
   * Validates SAPO Mail Meo Cloud limits
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {FilesEvent} files
   *
   * @return {Boolean}
   */
  validateFileLimits(files) {
    if (files.size > SAPOMailConfig.client.compose.meo_cloud.size_limit) {
      NotificationsController.addError('notifications.compose_meo_cloud_size_error');
      return false;
    }

    if (files.size + this.data.account.quota_info.used > this.data.account.quota_info.quota) {
      NotificationsController.addError('notifications.compose_meo_cloud_quota_error');
      return false;
    }

    return true;
  }
}
